import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService } from '../../services/api'
import type { Product, Category, PaginationParams, SearchFilters } from '../../types'

interface ProductState {
  products: Product[]
  categories: Category[]
  currentProduct: Product | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: SearchFilters
}

const initialState: ProductState = {
  products: [],
  categories: [],
  currentProduct: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
}

export const fetchProducts = createAsyncThunk(
  'product/fetchProducts',
  async ({ filters, pagination }: { filters?: SearchFilters; pagination?: PaginationParams }) => {
    const response = await apiService.getProducts(filters, pagination)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data
  }
)

export const fetchProductById = createAsyncThunk(
  'product/fetchProductById',
  async (id: string | number) => {
    const response = await apiService.getProduct(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.product
  }
)

export const createProduct = createAsyncThunk(
  'product/createProduct',
  async (productData: Partial<Product>) => {
    const response = await apiService.createProduct(productData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.product
  }
)

export const updateProduct = createAsyncThunk(
  'product/updateProduct',
  async ({ id, productData }: { id: string | number; productData: Partial<Product> }) => {
    const response = await apiService.updateProduct(id, productData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.product
  }
)

export const deleteProduct = createAsyncThunk(
  'product/deleteProduct',
  async (id: string | number) => {
    const response = await apiService.deleteProduct(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return id
  }
)

export const bulkDeleteProducts = createAsyncThunk(
  'product/bulkDeleteProducts',
  async (ids: number[]) => {
    const response = await apiService.bulkDeleteProducts(ids)
    if (response.error) {
      throw new Error(response.error)
    }
    return ids
  }
)

export const updateProductStock = createAsyncThunk(
  'product/updateProductStock',
  async ({ id, stock }: { id: string | number; stock: number }) => {
    const response = await apiService.updateProductStock(id, stock)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.product
  }
)

export const fetchCategories = createAsyncThunk(
  'product/fetchCategories',
  async () => {
    const response = await apiService.getCategories()
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.categories || []
  }
)

export const createCategory = createAsyncThunk(
  'product/createCategory',
  async ({ name, parentId, description }: { name: string; parentId?: number; description?: string }) => {
    const response = await apiService.createCategory(name, parentId, description)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.category
  }
)

export const updateCategory = createAsyncThunk(
  'product/updateCategory',
  async ({ id, data }: { id: string | number; data: Partial<Category> }) => {
    const response = await apiService.updateCategory(id, data)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.category
  }
)

export const deleteCategory = createAsyncThunk(
  'product/deleteCategory',
  async (id: string | number) => {
    const response = await apiService.deleteCategory(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return id
  }
)

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setCurrentProduct: (state, action: PayloadAction<Product | null>) => {
      state.currentProduct = action.payload
    },
    setFilters: (state, action: PayloadAction<SearchFilters>) => {
      state.filters = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<ProductState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false
        state.products = action.payload?.data || []
        state.pagination = {
          page: action.payload?.meta?.page || 1,
          limit: action.payload?.meta?.limit || 10,
          total: action.payload?.meta?.total || 0,
          totalPages: action.payload?.meta?.totalPages || 0,
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch products'
      })
      // Fetch product by id
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false
        state.currentProduct = action.payload || null
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch product'
      })
      // Create product
      .addCase(createProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.products.unshift(action.payload)
        }
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create product'
      })
      // Update product
      .addCase(updateProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          const payload = action.payload
          const index = state.products.findIndex((p) => p.id === payload.id)
          if (index !== -1) {
            state.products[index] = payload
          }
          if (state.currentProduct?.id === payload.id) {
            state.currentProduct = payload
          }
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update product'
      })
      // Delete product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false
        state.products = state.products.filter((p) => p.id !== action.payload)
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete product'
      })
      // Bulk delete products
      .addCase(bulkDeleteProducts.fulfilled, (state, action) => {
        state.products = state.products.filter((p) => !action.payload.includes(p.id as number))
        if (state.currentProduct && action.payload.includes(state.currentProduct.id as number)) {
          state.currentProduct = null
        }
      })
      // Update product stock
      .addCase(updateProductStock.fulfilled, (state, action) => {
        if (action.payload) {
          const payload = action.payload
          const index = state.products.findIndex((p) => p.id === payload.id)
          if (index !== -1) {
            state.products[index] = payload
          }
          if (state.currentProduct?.id === payload.id) {
            state.currentProduct = payload
          }
        }
      })
      // Fetch categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload
      })
      // Create category
      .addCase(createCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.categories.push(action.payload)
        }
      })
      // Update category
      .addCase(updateCategory.fulfilled, (state, action) => {
        if (action.payload) {
          const payload = action.payload
          const index = state.categories.findIndex((c) => c.id === payload.id)
          if (index !== -1) {
            state.categories[index] = payload
          }
        }
      })
      // Delete category
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((c) => c.id !== action.payload)
      })
  },
})

export const { setCurrentProduct, setFilters, setPagination, clearError } = productSlice.actions
export default productSlice.reducer
